import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setForm(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      const { data } = await api.put("/settings", payload);
      setForm(data);
      toast.success("Ajustes guardados");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  if (!form) return null;

  const setF = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div data-testid="settings-page">
      <PageHeader
        title="Ajustes"
        subtitle="Datos del emisor, certificado .p12 y método de valuación de inventario."
        testid="settings-header"
      />
      <form onSubmit={submit} className="space-y-6">
        <Card title="Datos del emisor (SRI)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="RUC"><input data-testid="settings-ruc" required className="input-soft" value={form.ruc} onChange={(e) => setF("ruc", e.target.value)} /></Field>
            <Field label="Razón social"><input className="input-soft" value={form.business_name} onChange={(e) => setF("business_name", e.target.value)} /></Field>
            <Field label="Nombre comercial"><input className="input-soft" value={form.trade_name} onChange={(e) => setF("trade_name", e.target.value)} /></Field>
            <Field label="Dirección matriz"><input className="input-soft" value={form.matriz_address} onChange={(e) => setF("matriz_address", e.target.value)} /></Field>
            <Field label="Establecimiento"><input className="input-soft" value={form.establishment} onChange={(e) => setF("establishment", e.target.value)} /></Field>
            <Field label="Punto de emisión"><input className="input-soft" value={form.emission_point} onChange={(e) => setF("emission_point", e.target.value)} /></Field>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Ambiente</span>
              <select className="input-soft" value={form.ambiente} onChange={(e) => setF("ambiente", e.target.value)}>
                <option value="1">Pruebas</option>
                <option value="2">Producción</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Obligado a llevar contabilidad</span>
              <select className="input-soft" value={form.obligado_contabilidad} onChange={(e) => setF("obligado_contabilidad", e.target.value)}>
                <option value="NO">NO</option>
                <option value="SI">SI</option>
              </select>
            </label>
          </div>
        </Card>

        <Card title="Certificado digital .p12">
          <p className="text-sm text-[#5C6B64] mb-4">
            Carga el archivo <code>.p12</code> en el servidor (ruta absoluta) y registra la contraseña aquí.
            Mientras el certificado no esté configurado, el sistema operará en <strong>modo simulación</strong>
            (las facturas se marcarán como autorizadas sin contactar al SRI).
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Ruta certificado (.p12)"><input className="input-soft" placeholder="/app/backend/certs/firma.p12" value={form.cert_path} onChange={(e) => setF("cert_path", e.target.value)} /></Field>
            <Field label="Contraseña del certificado"><input type="password" className="input-soft" placeholder="••••••••" onChange={(e) => setF("cert_password", e.target.value)} /></Field>
          </div>
        </Card>

        <Card title="Inventario / Kardex">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Método de valuación</span>
              <select data-testid="settings-valuation-method" className="input-soft" value={form.default_valuation_method} onChange={(e) => setF("default_valuation_method", e.target.value)}>
                <option value="promedio">Promedio ponderado</option>
                <option value="fifo">FIFO (primero en entrar, primero en salir)</option>
              </select>
            </label>
          </div>
        </Card>

        <Card title="Email (Resend)">
          <p className="text-sm text-[#5C6B64] mb-4">
            Configura el remitente que verán tus clientes al recibir las facturas autorizadas.
            Para usar un dominio propio, verifícalo en <a className="underline" href="https://resend.com/domains" target="_blank" rel="noreferrer">resend.com/domains</a>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label='Remitente "From"'><input data-testid="settings-email-from" className="input-soft" placeholder='Floristería <facturas@tudominio.com>' value={form.email_from || ""} onChange={(e) => setF("email_from", e.target.value)} /></Field>
            <Field label="Responder a (opcional)"><input type="email" className="input-soft" placeholder="contacto@tudominio.com" value={form.email_reply_to || ""} onChange={(e) => setF("email_reply_to", e.target.value)} /></Field>
          </div>
        </Card>

        <Card title="Landing pública y redes sociales">
          <p className="text-sm text-[#5C6B64] mb-4">
            Datos visibles en la pantalla de inicio pública (landing). El número de WhatsApp habilita el botón &ldquo;Pedido online&rdquo; y el FAB flotante.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="WhatsApp (solo dígitos, ej. 593987654321)">
              <input data-testid="settings-whatsapp" className="input-soft" placeholder="593987654321"
                value={form.whatsapp_phone || ""} onChange={(e) => setF("whatsapp_phone", e.target.value)} />
            </Field>
            <Field label="URL Facebook">
              <input data-testid="settings-facebook" className="input-soft" placeholder="https://facebook.com/tu-pagina"
                value={form.facebook_url || ""} onChange={(e) => setF("facebook_url", e.target.value)} />
            </Field>
            <Field label="URL Instagram">
              <input data-testid="settings-instagram" className="input-soft" placeholder="https://instagram.com/tu-cuenta"
                value={form.instagram_url || ""} onChange={(e) => setF("instagram_url", e.target.value)} />
            </Field>
            <Field label="URL imagen hero (landing)">
              <input className="input-soft" placeholder="https://..."
                value={form.public_hero_image || ""} onChange={(e) => setF("public_hero_image", e.target.value)} />
            </Field>
            <div className="md:col-span-2">
              <Field label="Tagline (frase principal de la landing)">
                <textarea rows={2} className="input-soft" placeholder="Arreglos florales que enamoran. Pídelos online."
                  value={form.public_tagline || ""} onChange={(e) => setF("public_tagline", e.target.value)} />
              </Field>
            </div>
          </div>
        </Card>

        <div className="flex justify-end">
          <button data-testid="settings-save-button" disabled={saving} className="btn-primary flex items-center gap-2" type="submit">
            <Save className="w-4 h-4" /> {saving ? "Guardando..." : "Guardar ajustes"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="card-soft p-6">
      <h3 className="font-heading text-lg font-bold mb-4 text-[#2A3F35]">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
